<script setup>
import { computed } from 'vue'
import {relativeUrl} from '@helpers/utils'

const emit = defineEmits(['update:selectedProduct'])

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
  selectedProduct: {
    type: Object,
    required: true,
  },
  flavors: {
    type: Object,
    default() {
      return {}
    },
  },
})

const hasMultipleProducts = computed(() => {
  return props.listing.products.length > 1
})
const hasFlavors = computed(() => {
  return Object.keys(props.flavors).length
})
</script>

<template>
  <div v-if="hasMultipleProducts || hasFlavors" class="product__variants">
    <div v-if="hasMultipleProducts" class="product__variant">
      <span v-if="listing.option_label" class="product__variant-title">{{ listing.option_label }}:</span>
      <div class="product__options">
        <a
          href="#"
          @click.prevent="emit('update:selectedProduct', product)"
          :class="['product__option', selectedProduct.id === product.id ? 'product__option--selected' : '']"
          v-for="product in listing.products"
          :key="product.id"
        >
          {{ product.option_value }}
        </a>
      </div>
    </div>
    <template v-if="hasFlavors">
      <span v-if="hasMultipleProducts" class="product__variant-title">More Options:</span>
      <div class="flavor__options">
        <a
          :class="['flavor__option', relativeUrl(option.url) === relativeUrl(listing.url) ? 'flavor__option--selected' : '']"
          :href="relativeUrl(option.url)"
          v-for="(option, flavor) in flavors"
          :key="flavor"
        >
          <img :src="option?.images?.[0]?.url" :alt="flavor" loading="lazy" />
          <span>{{ flavor }}</span>
        </a>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.product__variants {
  margin-top: 16px;

  @media screen and (min-width: 768px) {
    margin-top: 24px;
  }

  .product__variant {
    &:not(:last-of-type) {
      margin-bottom: 16px;
    }

    .product__variant-title {
      display: block;
      color: var(--neutral-1000, #3C3833);
      font-size: 16px;
      font-weight: 600;
      line-height: 28px; /* 175% */
      margin-bottom: 16px;

      @media screen and (min-width: 768px) {
        font-size: 18px;
        line-height: 32px;
        font-weight: 500;
      }
    }

    .product__options {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      @media screen and (min-width: 768px) {
        gap: 16px;
      }

      .product__option {
        border-radius: 16px;
        background: var(--neutral-50, #F5F5F5);
        color: var(--neutral-1000, #3C3833);
        text-align: center;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        padding: 8px 16px;
        text-decoration: none;
        white-space: nowrap;

        @media screen and (min-width: 768px) {
          font-size: 16px;
          line-height: 28px;
          padding: 4px 16px;
          border-radius: 47px;
        }

        &.product__option--selected, &.nuxt-link-active {
          background: var(--primary-600, #183D4F);
          color: var(--shades-white, #FFF);
        }
      }
    }
  }

  .flavor__options {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 13px;

    .flavor__option {
      width: 120px;
      min-width: 120px;
      display: flex;
      flex-direction: column;
      align-items: center;
      border-radius: 8px;
      background: #F5F5F5;
      color: var(--Light-navy, #183D4F);
      text-align: center;
      font-size: 16px;
      font-weight: 500;
      line-height: 18px;
      padding: 6px;
      text-decoration: none;
      word-break: break-word;

      @media screen and (min-width: 768px) {
        width: 110px;
        min-width: 110px;
        padding: 8px;
      }

      img {
        display: block;
        width: 62px;
        height: 65px;
      }

      &.flavor__option--selected {
        border: 2px solid #183D4F;
      }
    }
  }
}
</style>
